import { useCallback, useEffect, useReducer, useRef } from 'react';
import { Animated, Easing, Platform, Pressable, ScrollView, StyleSheet } from 'react-native';
import { SparkleBurstIcon } from '../../../components/icons/commandDeckIcons';
import { Box, Text } from '../../../components/primitives';
import { BentoSurface } from '../../../components/primitives/BentoSurface';
import { colors } from '../../../theme/colors';
import { shadows } from '../../../theme/shadows';
import { cardSurface } from '../../../theme/surfaces';
import { tokens } from '../../../theme/tokens';
import { darkPrecisionFontFamilies } from '../../../theme/typography';
import type { PendingApprovalAction, ProspectSearchMessage } from '../types';
import { ChatMessageBubble } from './ChatMessageBubble';
import { PendingActionCard } from './PendingActionCard';

interface ChatMessageListProps {
  readonly messages: ProspectSearchMessage[];
  readonly isSearching?: boolean;
  readonly searchingLabel?: string;
  readonly onSuggestionPress?: (suggestion: string) => void;
  readonly pendingAction?: PendingApprovalAction | null;
  readonly isResolvingPendingAction?: boolean;
  readonly onApprovePendingAction?: (itemIds?: readonly string[]) => void;
  readonly onRejectPendingAction?: () => void;
}

function TypingIndicator({ label = 'Searching...' }: { label?: string }) {
  const dot1 = useRef(new Animated.Value(0.3)).current;
  const dot2 = useRef(new Animated.Value(0.3)).current;
  const dot3 = useRef(new Animated.Value(0.3)).current;
  const labelOpacity = useRef(new Animated.Value(1)).current;
  // Hold the actual displayed label; we update it during the fade-out
  // so the new text only appears after the old one has faded away.
  const previousLabel = useRef(label);
  const [displayedLabel, setDisplayedLabel] = useDisplayedLabel(label);

  useEffect(() => {
    const createDotAnimation = (dot: Animated.Value, delay: number) =>
      Animated.loop(
        Animated.sequence([
          Animated.timing(dot, {
            toValue: 1,
            duration: 400,
            delay,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: true,
          }),
          Animated.timing(dot, {
            toValue: 0.3,
            duration: 400,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: true,
          }),
        ]),
      );

    const anim1 = createDotAnimation(dot1, 0);
    const anim2 = createDotAnimation(dot2, 150);
    const anim3 = createDotAnimation(dot3, 300);

    anim1.start();
    anim2.start();
    anim3.start();

    return () => {
      anim1.stop();
      anim2.stop();
      anim3.stop();
    };
  }, [dot1, dot2, dot3]);

  useEffect(() => {
    if (label === previousLabel.current) return;
    previousLabel.current = label;

    Animated.sequence([
      Animated.timing(labelOpacity, {
        toValue: 0,
        duration: 120,
        easing: Easing.inOut(Easing.ease),
        useNativeDriver: true,
      }),
      Animated.timing(labelOpacity, {
        toValue: 1,
        duration: 180,
        easing: Easing.inOut(Easing.ease),
        useNativeDriver: true,
      }),
    ]).start();

    // Swap the visible text at the midpoint of the crossfade, when
    // opacity has reached 0 — avoids a flash of overlapping characters.
    const swap = setTimeout(() => setDisplayedLabel(label), 120);
    return () => clearTimeout(swap);
  }, [label, labelOpacity, setDisplayedLabel]);

  return (
    <Box
      flexDirection="row"
      alignItems="center"
      gap="sm"
      paddingX="lg"
      paddingY="sm"
      minHeight={32}
    >
      <Box flexDirection="row" gap="xs">
        <Animated.View style={[typingStyles.dot, { opacity: dot1 }]} />
        <Animated.View style={[typingStyles.dot, { opacity: dot2 }]} />
        <Animated.View style={[typingStyles.dot, { opacity: dot3 }]} />
      </Box>
      <Animated.View style={[typingStyles.labelWrap, { opacity: labelOpacity }]}>
        <Text variant="caption" color={colors.text.tertiary} numberOfLines={1} ellipsizeMode="tail">
          {displayedLabel}
        </Text>
      </Animated.View>
    </Box>
  );
}

function useDisplayedLabel(initial: string) {
  const ref = useRef(initial);
  // Force re-render via a counter; cheap and avoids extra deps.
  // useReducer's dispatch is referentially stable, so wrapping the setter
  // in useCallback (with [] deps) gives us a stable function. Without
  // this, every parent re-render produced a new setter, which canceled
  // the pending fade-swap timeout via effect cleanup before it could
  // fire — the displayed label got stuck at its initial value during
  // active streaming.
  const [, force] = useReducer((x: number) => x + 1, 0);
  const setDisplayedLabel = useCallback((value: string) => {
    ref.current = value;
    force();
  }, []);
  return [ref.current, setDisplayedLabel] as const;
}

const typingStyles = StyleSheet.create({
  dot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: colors.primary.main,
  },
  labelWrap: {
    flex: 1,
  },
});

export function ChatMessageList({
  messages,
  isSearching,
  searchingLabel,
  onSuggestionPress,
  pendingAction,
  isResolvingPendingAction,
  onApprovePendingAction,
  onRejectPendingAction,
}: ChatMessageListProps) {
  const scrollRef = useRef<ScrollView>(null);

  useEffect(() => {
    // Empty state is centered, not a feed — auto-scrolling to the bottom
    // here pushes the brand icon and title off the top of the viewport.
    if (messages.length === 0 && !isSearching) return;
    const timer = setTimeout(() => {
      scrollRef.current?.scrollToEnd({ animated: true });
    }, 100);
    return () => clearTimeout(timer);
  });

  return (
    <ScrollView
      ref={scrollRef}
      style={styles.container}
      contentContainerStyle={styles.content}
      showsVerticalScrollIndicator={false}
      keyboardShouldPersistTaps="handled"
      keyboardDismissMode={Platform.OS === 'ios' ? 'interactive' : 'on-drag'}
    >
      {messages.length === 0 && !isSearching && (
        <Box flex={1} alignItems="center" justifyContent="center" style={styles.emptyState}>
          <Box style={styles.brandIcon}>
            <SparkleBurstIcon size={30} color={colors.primary[300]} strokeWidth={2} />
          </Box>
          <Box alignItems="center" style={styles.emptyCopy}>
            <Text variant="h4" color={colors.text.primary} style={styles.emptyTitle}>
              FlowSell Command Center
            </Text>
            <Text variant="body" color={colors.text.secondary} style={styles.emptyDescription}>
              Ask FlowSell to research prospects, qualify leads, summarize conversations, or take
              CRM actions while the specialist agents work in the background.
            </Text>
          </Box>
          <Box alignItems="center" style={styles.suggestionGroup}>
            {SUGGESTIONS.map((suggestion) => (
              <Pressable
                key={suggestion}
                onPress={() => onSuggestionPress?.(suggestion)}
                disabled={!onSuggestionPress || isSearching}
                style={({ pressed }) => [
                  styles.suggestion,
                  pressed && styles.suggestionPressed,
                  isSearching && styles.suggestionDisabled,
                ]}
                accessibilityRole="button"
                accessibilityLabel={`Use prompt: ${suggestion}`}
              >
                <BentoSurface radius={SUGGESTION_RADIUS} />
                <Text variant="caption" color={colors.text.secondary}>
                  {suggestion}
                </Text>
              </Pressable>
            ))}
          </Box>
        </Box>
      )}

      {messages.map((message, index) => (
        <ChatMessageBubble
          key={`${message.role}-${message.createdAt}-${index}`}
          message={message}
          index={index}
        />
      ))}

      {isSearching && <TypingIndicator label={searchingLabel} />}

      {pendingAction && onApprovePendingAction && onRejectPendingAction && (
        <PendingActionCard
          key={pendingAction.pendingActionId}
          action={pendingAction}
          isResolving={isResolvingPendingAction}
          onApprove={onApprovePendingAction}
          onReject={onRejectPendingAction}
        />
      )}
    </ScrollView>
  );
}

const SUGGESTIONS = [
  'Find top 100 chemical companies in Mexico',
  'Qualify my newest inbound leads',
  'Move qualified leads to the next funnel stage',
  'Summarize follow-up priorities for this week',
];

// Pill radius = half the 44px min-height so the ends fully round on iOS
// without the radius.full (9999) + border rounding fragility small bordered
// boxes hit on iOS.
const SUGGESTION_RADIUS = 22;

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    paddingVertical: 16,
    flexGrow: 1,
  },
  emptyState: {
    paddingHorizontal: 24,
    paddingVertical: 40,
    gap: 18,
  },
  emptyCopy: {
    maxWidth: 520,
  },
  brandIcon: {
    width: 64,
    height: 64,
    borderRadius: tokens.radius.xl,
    borderWidth: 1,
    borderColor: colors.primary.border,
    backgroundColor: colors.primary.muted,
    alignItems: 'center',
    justifyContent: 'center',
    ...(shadows.glowHaloSoft as object),
  },
  emptyTitle: {
    fontFamily: darkPrecisionFontFamilies.displayBold,
    fontSize: 26,
    lineHeight: 33,
    fontWeight: '800',
    letterSpacing: -0.6,
    textAlign: 'center',
  },
  emptyDescription: {
    textAlign: 'center',
    lineHeight: 24,
    marginTop: 12,
  },
  suggestionGroup: {
    gap: 12,
    marginTop: 8,
  },
  suggestion: {
    paddingHorizontal: 22,
    paddingVertical: 12,
    minHeight: 44,
    alignItems: 'center',
    justifyContent: 'center',
    ...cardSurface(),
    borderRadius: SUGGESTION_RADIUS,
    overflow: 'hidden',
  },
  suggestionPressed: {
    opacity: 0.72,
    borderColor: colors.primary.borderActive,
  },
  suggestionDisabled: {
    opacity: 0.48,
  },
});
